/**
 * Owner-only: issue role-based license codes (stored in InstitutionRoleLicenseBatches / InstitutionRoleLicenseCodes).
 */
import React, { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';

const OWNER_PRIMARY_EMAIL =
  (typeof process !== 'undefined' &&
    process.env &&
    String(process.env.REACT_APP_OWNER_PRIMARY_EMAIL || '').trim()) ||
  'james.houston@enterceptor.com';

const TRIAL_TERM_DAYS = 7;

/** License console page uses z-index ~26000; MUI Select menus default ~1300 and open behind it (locks the form). */
const LICENSE_CONSOLE_MENU_Z = 28000;
const LICENSE_AUDIENCE_MENU_PROPS = {
  disablePortal: true,
  disableScrollLock: true,
  sx: { zIndex: LICENSE_CONSOLE_MENU_Z },
  PaperProps: { sx: { zIndex: LICENSE_CONSOLE_MENU_Z } },
  MenuListProps: { sx: { maxHeight: 320 } }
};

function formatDt(v) {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

function audienceTypeLabel(audienceType, entityType, isTrialBatch) {
  if (isTrialBatch) return `${TRIAL_TERM_DAYS}-day trial`;
  const a = String(audienceType || '').toLowerCase();
  const e = String(entityType || '').toLowerCase();
  if (a === 'independent_learner' || e === 'individual') return 'Individual';
  if (a === 'owner_self') return 'Owner';
  if (a === 'owner_contributor') return 'Contributor';
  return 'Institution';
}

/** Local wall-clock value for <input type="datetime-local"> (not UTC from toISOString). */
function toLocalDatetimeInputValue(d = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** CSV download: Jane-Doe-batch-1.csv or Metro-Career-Technical-Center-batch-2.csv */
function licenseBatchCsvFilename(institutionName, batchId) {
  const slug =
    String(institutionName || 'license')
      .trim()
      .replace(/[^\w\s-]+/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 60) || 'license';
  return `${slug}-batch-${batchId}.csv`;
}

export default function OwnerLicenseConsole() {
  const [institutionName, setInstitutionName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [instructorName, setInstructorName] = useState('');
  const [startsAt, setStartsAt] = useState(() => toLocalDatetimeInputValue());
  const [termMonths, setTermMonths] = useState('12');
  const [isTrialBatch, setIsTrialBatch] = useState(false);
  /** institutional | owner_self | owner_contributor | independent_learner (student-only purchase) */
  const [adminSeatAudience, setAdminSeatAudience] = useState('institutional');
  const [adminSeats, setAdminSeats] = useState('0');
  const [teacherSeats, setTeacherSeats] = useState('0');
  const [studentSeats, setStudentSeats] = useState('1');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [batches, setBatches] = useState([]);
  const [selectedBatchIds, setSelectedBatchIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  const isIndependentLearner = adminSeatAudience === 'independent_learner';
  const batchSectionTitle = isIndependentLearner ? 'Independent learner' : 'Institution';
  const nameFieldLabel = isIndependentLearner ? 'Learner name' : 'Institution name';
  const namePlaceholder = isIndependentLearner
    ? 'e.g. Jane Doe'
    : 'e.g. Metro Career-Technical Center';
  const nameHelperText = isIndependentLearner
    ? 'Name for this individual purchase batch (stored as the institution record).'
    : 'School or district name for this license batch.';

  const totalSeats =
    (isIndependentLearner ? 0 : parseInt(adminSeats, 10) || 0) +
    (isIndependentLearner ? 0 : parseInt(teacherSeats, 10) || 0) +
    (parseInt(studentSeats, 10) || 0);

  const onAudienceChange = (next) => {
    setAdminSeatAudience(next);
    if (next === 'independent_learner') {
      setAdminSeats('0');
      setTeacherSeats('0');
      setInstructorName('');
      setStudentSeats((prev) => (parseInt(prev, 10) > 0 ? prev : '1'));
      setInstitutionName((prev) =>
        String(prev || '').trim().toLowerCase() === 'independent learner' ? '' : prev
      );
    }
  };

  const onTrialToggle = (checked) => {
    setIsTrialBatch(checked);
    if (checked) {
      setTermMonths('1');
      if (totalSeats > 1) {
        setAdminSeats('0');
        setTeacherSeats('0');
        setStudentSeats('1');
      }
    }
  };

  const loadBatches = useCallback(async () => {
    try {
      const r = await fetch('/api/admin/license-batches', { credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(data.error || 'Failed to load batches');
        return;
      }
      setBatches(Array.isArray(data.items) ? data.items : []);
      setError('');
    } catch {
      setError('Network error loading batches.');
    }
  }, []);

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  const submitBatch = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    const independent = adminSeatAudience === 'independent_learner';
    const adm = independent ? 0 : parseInt(adminSeats, 10) || 0;
    const tch = independent ? 0 : parseInt(teacherSeats, 10) || 0;
    const stu = parseInt(studentSeats, 10) || 0;
    const seatsTotal = adm + tch + stu;

    if (independent && stu <= 0) {
      setError('Independent learner batches need at least one student seat.');
      return;
    }
    if (isTrialBatch && !contactEmail.trim()) {
      setError('Trial accounts require a contact email — the code will only work for that Google sign-in.');
      return;
    }
    if (isTrialBatch && seatsTotal !== 1) {
      setError('Trial batches issue one code per person. Set total seats to exactly 1 (one admin, teacher, or student seat).');
      return;
    }
    if (adminSeatAudience === 'owner_contributor' && adm > 0 && !contactEmail.trim()) {
      setError('Enter the contributor contact email when issuing owner-contributor admin codes.');
      return;
    }
    setLoading(true);
    try {
      const startsIso = new Date(startsAt).toISOString();
      const r = await fetch('/api/admin/license-batches', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          institutionName: institutionName.trim(),
          contactEmail: contactEmail.trim(),
          instructorName: instructorName.trim(),
          startsAt: startsIso,
          termMonths: isTrialBatch ? 1 : parseInt(termMonths, 10) || 12,
          isTrialBatch,
          adminSeatAudience,
          adminSeats: adm,
          teacherSeats: tch,
          studentSeats: stu
        })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (data.error === 'owner_only') {
          setError(
            `Forbidden: owner license console only. Sign in as ${OWNER_PRIMARY_EMAIL} (or add your email to OWNER_ADMIN_EMAILS on the API).`
          );
          return;
        }
        setError(data.error ? String(data.error).replace(/_/g, ' ') : 'Create failed');
        return;
      }
      const c = data.counts || {};
      const trialNote = isTrialBatch
        ? ` ${TRIAL_TERM_DAYS}-day trial — locked to ${contactEmail.trim()}.`
        : '';
      setMessage(
        `Issued ${c.total ?? 0} codes (${c.admin ?? 0} admin, ${c.teacher ?? 0} teacher, ${c.student ?? 0} student).${trialNote} CSV export available below.`
      );
      setInstitutionName('');
      setContactEmail('');
      setInstructorName('');
      setStartsAt(toLocalDatetimeInputValue());
      setTermMonths('12');
      setIsTrialBatch(false);
      setAdminSeats('0');
      setTeacherSeats('0');
      setStudentSeats('1');
      await loadBatches();
    } catch {
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  };

  const toggleBatchSelected = (batchId) => {
    setSelectedBatchIds((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  };

  const allBatchIds = batches.map((b) => b.Id);
  const allSelected = allBatchIds.length > 0 && allBatchIds.every((id) => selectedBatchIds.has(id));
  const someSelected = allBatchIds.some((id) => selectedBatchIds.has(id));

  const toggleSelectAllBatches = () => {
    if (allSelected) {
      setSelectedBatchIds(new Set());
      return;
    }
    setSelectedBatchIds(new Set(allBatchIds));
  };

  const deleteSelectedBatches = async () => {
    const ids = [...selectedBatchIds];
    if (!ids.length) {
      setError('Select one or more batches (checkbox next to ID) to delete.');
      return;
    }
    const ok = window.confirm(
      `Delete ${ids.length} license batch${ids.length === 1 ? '' : 'es'}?\n\n` +
        `Unused codes are permanently deleted and cannot be redeemed.\n` +
        `${TRIAL_TERM_DAYS}-day trial batches also remove redeemed seats (even if they only signed in once / never started) and end that trial access.\n` +
        `Institution batches keep already-redeemed school seats.`
    );
    if (!ok) return;
    setDeleting(true);
    setMessage('');
    setError('');
    try {
      const r = await fetch('/api/admin/license-codes/revoke', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ batchIds: ids })
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(data.error ? String(data.error).replace(/_/g, ' ') : 'Delete failed');
        return;
      }
      const deleted = data.deleted ?? data.revoked ?? 0;
      const batchesGone = data.deletedBatches ?? 0;
      const detached = data.detachedTrialUsers ?? 0;
      setMessage(
        `Deleted ${deleted} license code${deleted === 1 ? '' : 's'} from the database` +
          (batchesGone ? ` and removed ${batchesGone} empty batch${batchesGone === 1 ? '' : 'es'}` : '') +
          (detached ? ` (ended ${detached} trial seat${detached === 1 ? '' : 's'})` : '') +
          `. They can no longer be redeemed.`
      );
      setSelectedBatchIds(new Set());
      await loadBatches();
    } catch {
      setError('Network error deleting licenses.');
    } finally {
      setDeleting(false);
    }
  };

  const downloadCsv = async (batchId, institutionNameForFile) => {
    try {
      const r = await fetch(`/api/admin/license-batches/${batchId}/codes.csv`, { credentials: 'include' });
      if (!r.ok) {
        setError('Export failed.');
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = licenseBatchCsvFilename(institutionNameForFile, batchId);
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Export failed.');
    }
  };

  return (
    <Box
      className="notranslate"
      translate="no"
      dir="ltr"
      lang="en"
      sx={{ height: '100%', overflow: 'auto', p: 1, bgcolor: '#040810' }}
    >
      <Typography variant="subtitle2" sx={{ color: '#a8e6ff', mb: 1, letterSpacing: '0.06em' }}>
        Issue licenses by role
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(200, 230, 255, 0.75)', mb: 2 }}>
        Codes are stored in the database and redeemed at sign-in. Use <strong>Trial account</strong> for {TRIAL_TERM_DAYS}-day intro access locked to one email. Standard batches use the term in months.
      </Typography>

      <Paper
        component="form"
        onSubmit={submitBatch}
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          bgcolor: '#121c2f',
          border: '1px solid rgba(120, 200, 255, 0.28)',
          boxShadow: 'none',
          '& .MuiOutlinedInput-root': {
            bgcolor: '#0e1626'
          },
          '& .MuiOutlinedInput-input': {
            color: '#e8f4ff',
            WebkitTextFillColor: '#e8f4ff',
            caretColor: '#e8f4ff'
          },
          '& .MuiInputLabel-root': { color: 'rgba(200, 230, 255, 0.75)' },
          '& .MuiInputLabel-root.Mui-focused': { color: '#a8e6ff' },
          '& .MuiSelect-icon': { color: 'rgba(200, 230, 255, 0.85)' }
        }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <FormControl size="small" sx={{ gridColumn: { sm: 'span 2' } }}>
            <InputLabel id="admin-seat-audience-label">License audience</InputLabel>
            <Select
              labelId="admin-seat-audience-label"
              label="License audience"
              value={adminSeatAudience}
              onChange={(e) => onAudienceChange(e.target.value)}
              MenuProps={LICENSE_AUDIENCE_MENU_PROPS}
            >
              <MenuItem value="institutional">School leadership (Instructor / Principal / Superintendent)</MenuItem>
              <MenuItem value="independent_learner">Independent learner (student purchase only)</MenuItem>
              <MenuItem value="owner_self">Owner (me) — anchor admin codes</MenuItem>
              <MenuItem value="owner_contributor">Owner console contributor (named contact)</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ gridColumn: { sm: 'span 2' }, mt: -0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isTrialBatch}
                  onChange={(e) => onTrialToggle(e.target.checked)}
                  sx={{ color: '#67e8f9', '&.Mui-checked': { color: '#22d3ee' } }}
                />
              }
              label={
                <Typography variant="body2" sx={{ color: '#e8f4ff', fontWeight: 700 }}>
                  Trial account ({TRIAL_TERM_DAYS}-day access)
                </Typography>
              }
            />
            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(200, 230, 255, 0.72)', ml: 4.25 }}>
              {isTrialBatch
                ? `Access ends ${TRIAL_TERM_DAYS} days after start. Contact email is required and locks the code to that Google account. Issue one seat per recipient.`
                : 'Uncheck for standard annual or custom-term school licenses.'}
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{ gridColumn: { sm: 'span 2' }, color: 'rgba(200, 230, 255, 0.72)', mt: -1 }}
          >
            {adminSeatAudience === 'owner_self' && (parseInt(adminSeats, 10) || 0) > 0
              ? `If contact email is empty, the API uses ${OWNER_PRIMARY_EMAIL} so your chosen term (1–60 months) applies.`
              : null}
            {adminSeatAudience === 'owner_contributor' && (parseInt(adminSeats, 10) || 0) > 0
              ? 'Enter that person’s email in Contact email (required for this audience).'
              : null}
            {!isTrialBatch && adminSeatAudience === 'institutional'
              ? 'Use Contact email optionally for the school’s billing or ops contact.'
              : null}
            {isIndependentLearner
              ? 'Self-serve / individual purchase: student seats only. Admin and teacher seats are locked at 0. Put the buyer email below when known.'
              : null}
            {adminSeatAudience === 'owner_self' && !(parseInt(adminSeats, 10) > 0)
              ? 'Owner anchor codes — set admin seats when issuing codes for yourself.'
              : null}
            {adminSeatAudience === 'owner_contributor' && !(parseInt(adminSeats, 10) > 0)
              ? 'Named contributor — set admin seats and contact email when issuing codes.'
              : null}
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{
              gridColumn: { sm: 'span 2' },
              color: '#a8e6ff',
              letterSpacing: '0.08em',
              fontWeight: 700,
              mt: 0.5,
              mb: -0.5
            }}
          >
            {batchSectionTitle}
          </Typography>

          <TextField
            label={nameFieldLabel}
            value={institutionName}
            onChange={(e) => setInstitutionName(e.target.value)}
            required
            size="small"
            sx={{ gridColumn: { sm: 'span 2' } }}
            placeholder={namePlaceholder}
            InputLabelProps={{ shrink: true }}
            helperText={nameHelperText}
          />
          <TextField
            label={
              isTrialBatch
                ? 'Recipient email (required for trial)'
                : isIndependentLearner
                  ? 'Buyer email (optional)'
                  : 'Contact email (optional)'
            }
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            required={isTrialBatch}
            size="small"
            helperText={
              isTrialBatch
                ? 'Only this Google account can redeem the trial code.'
                : undefined
            }
          />
          <TextField
            label="Instructor name (optional)"
            value={instructorName}
            onChange={(e) => setInstructorName(e.target.value)}
            size="small"
            placeholder="e.g. Jane Smith"
            InputLabelProps={{ shrink: true }}
            helperText={
              isIndependentLearner
                ? 'Optional — usually blank for individual purchases.'
                : 'Lead instructor for this school batch (saved with the license record).'
            }
            disabled={isIndependentLearner}
          />
          <TextField
            label="Starts at"
            type="datetime-local"
            value={startsAt}
            onChange={(e) => setStartsAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label={isTrialBatch ? `Term (${TRIAL_TERM_DAYS} days — fixed)` : 'Term (months)'}
            type={isTrialBatch ? 'text' : 'number'}
            inputProps={isTrialBatch ? { readOnly: true } : { min: 1, max: 60 }}
            value={isTrialBatch ? String(TRIAL_TERM_DAYS) : termMonths}
            onChange={(e) => setTermMonths(e.target.value)}
            size="small"
            disabled={isTrialBatch}
          />
          <TextField
            label="Admin seats"
            type="number"
            inputProps={{ min: 0, max: isTrialBatch ? 1 : undefined }}
            value={isIndependentLearner ? '0' : adminSeats}
            onChange={(e) => setAdminSeats(e.target.value)}
            size="small"
            disabled={isIndependentLearner}
          />
          <TextField
            label="Teacher seats"
            type="number"
            inputProps={{ min: 0, max: isTrialBatch ? 1 : undefined }}
            value={isIndependentLearner ? '0' : teacherSeats}
            onChange={(e) => setTeacherSeats(e.target.value)}
            size="small"
            disabled={isIndependentLearner}
          />
          <TextField
            label="Student seats"
            type="number"
            inputProps={{ min: isIndependentLearner ? 1 : 0, max: isTrialBatch ? 1 : undefined }}
            value={studentSeats}
            onChange={(e) => setStudentSeats(e.target.value)}
            size="small"
            required={isIndependentLearner}
          />
        </Box>
        <Button type="submit" variant="contained" sx={{ mt: 2 }} disabled={loading}>
          {loading ? 'Creating…' : isTrialBatch ? 'Generate trial code' : 'Generate license codes'}
        </Button>
        {message ? (
          <Typography variant="body2" sx={{ mt: 1.5, color: '#a8f5c8' }}>
            {message}
          </Typography>
        ) : null}
        {error ? (
          <Typography variant="body2" sx={{ mt: 1, color: '#ffb4a8' }}>
            {error}
          </Typography>
        ) : null}
      </Paper>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ color: '#a8e6ff' }}>
          Recent batches
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="error"
          disabled={deleting || !someSelected}
          onClick={deleteSelectedBatches}
          sx={{ textTransform: 'none', fontWeight: 800 }}
        >
          {deleting ? 'Deleting…' : `Delete selected${someSelected ? ` (${selectedBatchIds.size})` : ''}`}
        </Button>
      </Box>
      <Typography variant="caption" sx={{ display: 'block', color: 'rgba(200,230,255,0.65)', mb: 1 }}>
        Check batches by ID, then Delete — unused codes are removed; trial batches also clear redeemed never-started seats.
      </Typography>
      <Table size="small" sx={{ '& td, & th': { borderColor: 'rgba(120,200,255,0.2)', color: '#e0f4ff', fontSize: '0.78rem' } }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" sx={{ width: 42 }}>
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={toggleSelectAllBatches}
                sx={{ color: 'rgba(180,220,255,0.7)', '&.Mui-checked': { color: '#7dd3fc' } }}
                inputProps={{ 'aria-label': 'Select all license batches' }}
              />
            </TableCell>
            <TableCell>ID</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Contact</TableCell>
            <TableCell>Instructor</TableCell>
            <TableCell align="right">Adm</TableCell>
            <TableCell align="right">Tch</TableCell>
            <TableCell align="right">Stu</TableCell>
            <TableCell>Ends</TableCell>
            <TableCell align="right">CSV</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {batches.map((b) => (
            <TableRow key={b.Id} selected={selectedBatchIds.has(b.Id)}>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={selectedBatchIds.has(b.Id)}
                  onChange={() => toggleBatchSelected(b.Id)}
                  sx={{ color: 'rgba(180,220,255,0.7)', '&.Mui-checked': { color: '#7dd3fc' } }}
                  inputProps={{ 'aria-label': `Select batch ${b.Id}` }}
                />
              </TableCell>
              <TableCell>{b.Id}</TableCell>
              <TableCell>{audienceTypeLabel(b.AudienceType, b.EntityType, b.IsTrialBatch)}</TableCell>
              <TableCell>{b.InstitutionName}</TableCell>
              <TableCell>{b.ContactEmail || '—'}</TableCell>
              <TableCell>{b.InstructorName || '—'}</TableCell>
              <TableCell align="right">{b.AdminSeats}</TableCell>
              <TableCell align="right">{b.TeacherSeats}</TableCell>
              <TableCell align="right">{b.StudentSeats}</TableCell>
              <TableCell>{formatDt(b.EndsAt)}</TableCell>
              <TableCell align="right">
                <Button size="small" onClick={() => downloadCsv(b.Id, b.InstitutionName)}>
                  Download
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!batches.length ? (
        <Typography variant="caption" sx={{ color: 'rgba(200,220,240,0.6)' }}>
          No batches yet — issue seats above.
        </Typography>
      ) : null}
    </Box>
  );
}
